/**
 * Who is on the home page, and the order of the menu.
 *
 * These three lists used to be `const`s in `src/index.11tydata.js` and
 * `lib/wf-nav.js`, so putting a new colleague on the home page was a deploy.
 * They live in `content/site/home.json` now, and this screen writes it.
 *
 * Only Live items are offered, because the build refuses to render a pinned
 * item that is not Live — it throws rather than drawing a blank card, which is
 * the right behaviour and a confusing way to find out. A review is offered by
 * its reviewer and rating, the way the card will read.
 *
 * The file also holds `reviewsNotice`, the one line the colleges ask for
 * under the reviews (ADR-0016): empty until the clinic has wording, and
 * printed nowhere while it is.
 */
import React, { useEffect, useState } from "react";

import { ApiError, api } from "../api.js";
import { Message } from "../components/bits.jsx";

export default function HomePicks() {
  const [data, setData] = useState(null);
  const [picks, setPicks] = useState(null);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("home")
      .then((next) => {
        setData(next);
        setPicks(structuredClone(next.picks));
      })
      .catch((problem) => setState({ level: "error", text: problem instanceof ApiError ? problem.message : "Could not load." }));
  }, []);

  async function save() {
    setBusy(true);
    setState(null);
    try {
      await api.put("home", { picks });
      setState({ level: "ok", text: "Saved. The home page updates when the build finishes." });
    } catch (problem) {
      setState({ level: "error", text: problem instanceof ApiError ? problem.message : "Could not save." });
    } finally {
      setBusy(false);
    }
  }

  if (!picks) return <div className="empty">Loading…</div>;

  const choose = (list, index, value) => {
    const next = structuredClone(picks);
    if (list === "review") next.featuredReviews[index] = value;
    else next.navAssessments[index] = value;
    setPicks(next);
  };

  const chooseFace = (tab, index, value) => {
    const next = structuredClone(picks);
    next.teamTabs[tab].faces[index] = value;
    setPicks(next);
  };

  const options = (list) =>
    (data.choices[list] || []).map((o) => (
      <option key={o.slug} value={o.slug}>
        {o.name}
      </option>
    ));

  return (
    <>
      <div className="head">
        <div>
          <h1>Home page</h1>
          <p className="sub">The faces, the reviews and the line under them, and the order of the menu.</p>
        </div>
      </div>

      <div className="card">
        <header>
          <h2>The four team tabs</h2>
          <p className="help">Three people under each. Everyone else still appears on /providers.</p>
        </header>
        <div className="body stack">
          {picks.teamTabs.map((tab, t) => (
            <div key={tab.title}>
              <h3>{tab.title}</h3>
              <div className="row" style={{ marginTop: "0.4rem" }}>
                {tab.faces.map((face, f) => (
                  <select key={f} value={face} onChange={(e) => chooseFace(t, f, e.target.value)} style={{ flex: "1 1 12rem" }} aria-label={`${tab.title}, position ${f + 1}`}>
                    {options("provider")}
                  </select>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <header>
          <h2>The three reviews</h2>
          <p className="help">Chosen, not newest-first — that has always been an editorial decision.</p>
        </header>
        <div className="body row">
          {picks.featuredReviews.map((slug, i) => (
            <select key={i} value={slug} onChange={(e) => choose("review", i, e.target.value)} style={{ flex: "1 1 12rem" }} aria-label={`Review ${i + 1}`}>
              {options("testimonials")}
            </select>
          ))}
        </div>
      </div>

      <div className="card">
        <header>
          <h2>The line under the reviews</h2>
          <p className="help">
            Printed under the reviews on the home page and on /testimonials, and nowhere while it is empty. The
            physicians' college (CPSBC) asks that testimonials name the responsible licensee and state the MSP-funding
            position; the wording is the clinic's, once it has the college's confirmation.
          </p>
        </header>
        <div className="body">
          <div className="field">
            <label htmlFor="reviews-notice">Notice</label>
            <textarea
              id="reviews-notice"
              rows={2}
              value={picks.reviewsNotice || ""}
              onChange={(e) => setPicks({ ...picks, reviewsNotice: e.target.value })}
              placeholder="Nothing is printed while this is empty."
            />
            <p className="help">One or two plain sentences. Saved as text, so nothing here becomes a link or a heading.</p>
          </div>
        </div>
      </div>

      <div className="card">
        <header>
          <h2>The menu's order</h2>
          <p className="help">How the header lists the seven assessments, on every page of the site.</p>
        </header>
        <div className="body stack">
          {picks.navAssessments.map((slug, i) => (
            <div className="row" key={i}>
              <span className="hint" style={{ width: "1.5rem" }}>{i + 1}.</span>
              <select value={slug} onChange={(e) => choose("nav", i, e.target.value)} style={{ flex: 1 }} aria-label={`Menu position ${i + 1}`}>
                {options("assessment")}
              </select>
            </div>
          ))}
        </div>
      </div>

      <div className="sticky-actions row">
        <button className="primary" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save the picks"}
        </button>
        {state && <Message level={state.level}>{state.text}</Message>}
      </div>
    </>
  );
}
